import React, { useContext, useEffect, useState, useRef } from "react";
import Footer from "../Footer";
import _ from "lodash";
import TablePagination from "../Common/Pagination";
import axiosInstance from "../../utils/Requests/axios";
import toast from "react-hot-toast";
import UserContext from "../../utils/Contexts/UserContext";
import TransactionsTable from "./TransactionsTable";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import ArrowCycle from "../svg/ArrowCycle";
import AppleTransaction from "../svg/AppleTransaction";
import GoogleTransaction from "../svg/GoogleTransaction";
import PaypalTransaction from "../svg/paypalTransaction";
import CardTransaction from "../svg/CardTransaction";
import BalanceTypeModal from "../../modals/BalanceTypeModal";
import FilterIcon from "../svg/Filter";

function Transactions() {
  const { userData } = useContext(UserContext);
  const { t } = useContext(LanguageContext);

  const [pageCount, setPageCount] = useState(15);
  const [activePage, setActivePage] = useState(1);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [activeBalanceTab, setActiveBalanceTab] = useState(null);
  const [activeMethodTab, setActiveMethodTab] = useState(null);
  const [modalFilters, setModalFilters] = useState([]);
  const [columns, setColumns] = useState({
    id: true,
    description: true,
    payment_method: true,
    status: true,
    amount: true,
    date: true,
    hold_until: true,
  });
  const [formData, setFormData] = useState({
    draw: 1,
    columns: [
      { data: "code" },
      { data: "method" },
      { data: "status" },
      { data: "amount" },
      { data: "created" },
      { data: "hold_until_date" },
    ],
    order: [{ column: 4, dir: "desc" }],
    start: 0,
    length: 15,
    search: {
      value: "",
      filters: [],
    },
  });

  useEffect(() => {
    const payload = { ...formData };
    const start = activePage === 1 ? 0 : (activePage - 1) * pageCount;
    if (payload.start !== start || payload.length !== pageCount) {
      payload.start = start;
      payload.length = pageCount;
      setFormData(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageCount]);

  const fetchTransactions = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/transactions/search`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        const data = response?.data?.data || [];
        const filtered = response?.data?.recordsFiltered || 0;
        setTableData(data);
        setRecordsFiltered(filtered);
      })
      .catch(() => {
        toast.error(t("There was an error fetching backlinks offers"));
      });
  };

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      setTableData(null);
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const [columnSorts, setColumnSorts] = useState({
    0: null,
    1: null,
    2: null,
    3: null,
    4: "desc",
    5: null,
  });

  useEffect(() => {
    const order = Object.keys(columnSorts)
      .filter((columnIndex) => columnSorts[columnIndex])
      .map((columnIndex) => ({
        column: Number(columnIndex),
        dir: columnSorts[columnIndex],
      }));

    if (!_.isEqual(formData.order, order)) {
      setFormData((prev) => ({
        ...prev,
        order,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSorts]);

  const BalanceType = [
    { value: 7, name: t("Non-Withdrawable") },
    { value: 1, name: t("Spending Credits") },
    { value: 2, name: t("Income") },
    { value: 3, name: t("Warranty Hold") },
    { value: 5, name: t("Withdrawal") },
    { value: 8, name: t("External") },
  ];

  const MethodType = [
    { value: 1, name: t("Internal"), content: <ArrowCycle /> },
    { value: 2, name: t("Card"), content: <CardTransaction /> },
    { value: 3, content: <PaypalTransaction /> },
    { value: 4, content: <GoogleTransaction /> },
    { value: 5, content: <AppleTransaction /> },
  ];

  const handleToggle = (column) => {
    setColumns((prevColumns) => ({
      ...prevColumns,
      [column]: !prevColumns[column],
    }));
  };

  const handleFilterClick = (filterKey, value) => {
    setFormData((prevData) => {
      let updatedFilters;

      if (value === "ALL") {
        // Remove the filter completely
        updatedFilters = prevData.search.filters.filter(
          (filter) => filter.key !== filterKey
        );
      } else {
        // Update or add the filter
        updatedFilters = [
          ...prevData.search.filters.filter(
            (filter) => filter.key !== filterKey
          ),
          { key: filterKey, value: value },
        ];
      }
      return {
        ...prevData,
        length: 15,
        search: {
          ...prevData.search,
          filters: updatedFilters,
        },
      };
    });

    if (filterKey === "BALANCE_TYPE") {
      setActiveBalanceTab(value === "ALL" ? null : value);
    } else if (filterKey === "PAYMENT_METHOD") {
      setActiveMethodTab(value === "ALL" ? null : value);
    }
  };

  useEffect(() => {
    const lastPage = Math.ceil(recordsFiltered / pageCount);
    if (activePage !== 1 && activePage > lastPage) {
      setActivePage(lastPage || 1);
    }
  }, [recordsFiltered, pageCount, activePage]);

  const updatePendingFilter = (key, value) => {
    setModalFilters((prevFilters) => {
      const updatedFilters = prevFilters.map((filter) =>
        filter.key === key ? { key, value } : filter
      );

      const finalFilters = prevFilters.some((filter) => filter.key === key)
        ? updatedFilters
        : [...prevFilters, { key, value }];

      return finalFilters;
    });

    if (key === "BALANCE_TYPE") {
      setActiveBalanceTab(value === "ALL" ? null : value);
    } else if (key === "METHOD") {
      setActiveMethodTab(value === "ALL" ? null : value);
    }
  };

  return (
    <main id="content" role="main" className="main">
      <div className="content container-fluid">
        <div
          className="page-header pb-0 mb-0"
          style={{ borderBottom: "unset" }}
        >
          <div className="row align-items-center mb-1">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb breadcrumb-no-gutter">
                <li className="breadcrumb-item">
                  {/* eslint-disable-next-line */}
                  <a className="breadcrumb-link" href="javascript:;">
                    {t("Wallet")}
                  </a>
                  <h1 className="page-header-title">{t("Transactions")}</h1>
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div
          className="mb-5"
          style={{
            borderBottom: ".0625rem solid var(--border)",
            marginLeft: "-30px",
            marginRight: "-30px",
          }}
        ></div>
        <div className="card">
          <div className="card-header card-header-content-between d-flex flex-wrap">
            <div className="mb-2 mb-md-0 js-nav-scroller hs-nav-scroller-horizontal d-flex gap-3 flex-wrap">
              <div className="text-sm-center d-none d-xxl-block">
                <ul
                  className="nav nav-segment flex-wrap flex-sm-nowrap"
                  role="tablist"
                  style={{ width: "798px", height: "48px" }}
                >
                  <li>
                    <a
                      id="nav-one-eg1-tab"
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      role="tab"
                      aria-controls="nav-one-eg1"
                      className={`nav-link ${
                        !activeBalanceTab ? "active" : ""
                      } `}
                      onClick={() => handleFilterClick("BALANCE_TYPE", "ALL")}
                      data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                      style={{ fontSize: "16px", fontWeight: "600" }}
                    >
                      {t("All Types")}
                    </a>
                  </li>
                  {BalanceType.map((balance) => (
                    <li className="nav-item" key={balance.value}>
                      <a
                        className={`js-loading-state-invoker nav-link ${
                          activeBalanceTab === balance.value ? "active" : ""
                        }`}
                        id="nav-one-eg1-tab"
                        data-bs-toggle="pill"
                        href="#nav-one-eg1"
                        role="tab"
                        aria-controls="nav-one-eg1"
                        onClick={() =>
                          handleFilterClick("BALANCE_TYPE", balance.value)
                        }
                        data-hs-loading-state-options='{
                          "targetEl": "#nav-one-eg1",
                          "removeLoaderDelay": "300"
                        }'
                        style={{
                          color: "#919293",
                          fontSize: "16px",
                          fontWeight: "600",
                        }}
                      >
                        {balance.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-sm-center d-none d-md-block">
                <ul
                  className="nav nav-segment justify-content-between flex-wrap flex-sm-nowrap"
                  role="tablist"
                  style={{ width: "477px", height: "48px" }}
                >
                  <li>
                    <a
                      id="nav-one-eg1-tab"
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      className={`nav-link ${
                        !activeMethodTab ? "active" : ""
                      } `}
                      onClick={() => handleFilterClick("PAYMENT_METHOD", "ALL")}
                      data-hs-loading-state-options='{
                        "targetEl": "#nav-one-eg1",
                        "removeLoaderDelay": "300"
                      }'
                      style={{ fontSize: "16px" }}
                    >
                      {t("All")}
                    </a>
                  </li>
                  {MethodType.map((method) => (
                    <li className="nav-item" key={method.value}>
                      <a
                        className={`js-loading-state-invoker nav-link ${
                          activeMethodTab === method.value ? "active" : ""
                        }`}
                        id="nav-one-eg1-tab"
                        data-bs-toggle="pill"
                        href="#nav-one-eg1"
                        role="tab"
                        aria-controls="nav-one-eg1"
                        aria-selected={activeMethodTab === method.value}
                        onClick={() =>
                          handleFilterClick("PAYMENT_METHOD", method.value)
                        }
                        data-hs-loading-state-options='{
                            "targetEl": "#nav-one-eg1",
                            "removeLoaderDelay": "300"
                          }'
                      >
                        {method.content}{" "}
                        <span className="fs-5 align-middle">{method.name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="w-100 w-md-25 w-xl-auto d-grid d-flex justify-content-between justify-content-md-end align-items-center gap-2">
              <div className="btn-group dropupend d-block d-xxl-none">
                <button
                  className="btn btn-white text-nowrap"
                  type="button"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#offcanvasBalanceFilter"
                  aria-controls="offcanvasBalanceFilterLabel"
                  style={{ fontSize: "16px" }}
                >
                  <FilterIcon /> {t("Filters")}{" "}
                  {modalFilters.length > 0 && `(${modalFilters.length})`}
                </button>
              </div>
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-white w-100 text-nowrap "
                  id="showHideDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  data-bs-auto-close="outside"
                  style={{ fontSize: "16px" }}
                >
                  <i className="bi bi-layout-three-columns me-1"></i>{" "}
                  {t("Columns")}
                </button>

                <div
                  className="dropdown-menu dropdown-menu-end dropdown-card"
                  aria-labelledby="showHideDropdown"
                  style={{ width: "15rem" }}
                >
                  <div className="card card-sm">
                    <div className="card-body">
                      <div className="d-grid gap-3">
                        {Object.keys(columns).map((key) => (
                          <label
                            key={key}
                            className="row form-check form-switch"
                            htmlFor={`toggleColumn_${key}`}
                          >
                            <span className="col-8 col-sm-9 ms-0">
                              <span className="me-2">
                                {t(_.startCase(key))}
                              </span>
                            </span>
                            <span className="col-4 col-sm-3 text-end">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`toggleColumn_${key}`}
                                checked={columns[key]}
                                onChange={() => handleToggle(key)}
                              />
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <TransactionsTable
            data={tableData}
            columnsVisibility={columns}
            setColumnSorts={setColumnSorts}
            columnSorts={columnSorts}
          />

          <TablePagination
            activePage={activePage}
            setActivePage={setActivePage}
            recordsFiltered={recordsFiltered}
            setPageCount={setPageCount}
            pageCount={pageCount}
          />
        </div>
      </div>

      <Footer />
      <BalanceTypeModal
        setFormData={setFormData}
        updatePendingFilter={updatePendingFilter}
        modalFilters={modalFilters}
        BalanceType={BalanceType}
        MethodType={MethodType}
        activeBalanceTab={activeBalanceTab}
        activeMethodTab={activeMethodTab}
        setActiveMethodTab={setActiveMethodTab}
        setActiveBalanceTab={setActiveBalanceTab}
        setModalFilters={setModalFilters}
        setActivePage={setActivePage}
      />
    </main>
  );
}

export default Transactions;
